import React, { useEffect, useState } from 'react';
import ReForm, { type NestedObject, type JsonFormsData } from '@restore/reform';
import { Box, Container, styled } from '@mui/system';
import { useSettings } from 'src/hooks/use-settings';
import { schema } from './schema';
import { uischema } from './uischema';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { paths, permissions } from 'src/paths';
import { datadogRum } from '@datadog/browser-rum';
import { getRealmName } from 'src/routes/AuthContext';
import SucessModal from '../sucess-modal';
import CommonLoader from 'src/utils/common-loader';
import DocumentService from 'src/services/documentService';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  SvgIcon,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment';
import { CodeSubmission, IcdCode } from 'src/services/documentService/types';
import useCurrentUser from 'src/hooks/use-current-user';
import {
  codesApiUrl,
  codesApiSecurityCode,
  excludes1WarnTreatment,
  icd10ReturnToProvider,
} from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';

const { patients, patientDetails, episodeOfCare, details, addNewEpisodeOfCare } = paths;

type FormStatus = 'loading' | 'ready' | 'submitting' | 'success' | 'error';

const DetailsContainer = styled('div')(() => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  gap: '1em',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const PatientInfoContainer = styled('div')(() => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  gap: '1em',
  flexGrop: 1,
}));

const MedicalCodeEditor: React.FC = () => {
  const [formStatus, setFormStatus] = useState<FormStatus>('loading');
  const [prefill, setPrefill] = useState<NestedObject>({});
  const [formData, setFormData] = useState<JsonFormsData>(prefill || {});
  const [errorMessage, setErrorMessage] = useState<string>('Something went wrong');
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [doNotDisplayChecked, setDoNotDisplayChecked] = useState<boolean>(false);
  const [dialogDisabled, setDialogDisabled] = useState<boolean>(false);
  const settings = useSettings();
  const [providerId] = useCurrentUser(['providerId']);
  const navigate = useNavigate();
  const realm = getRealmName();
  const location = useLocation();

  const [excludes1WarnEnabled] = useFeatureToggleWhenReady(excludes1WarnTreatment, 'FACILITY');

  const [icd10ReturnToProviderEnabled] = useFeatureToggleWhenReady(
    icd10ReturnToProvider,
    'FACILITY'
  );

  const doNotDisplayDialog = () => {
    const doNotDisplay = localStorage.getItem(`doNotDisplay${providerId}`);

    return doNotDisplay === 'true' || false;
  };

  const therapistProfile = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  const { EDIT_MEDICAL_CODE } = permissions;

  const facilityId = localStorage.getItem('facilityId');
  const patientData = location.state.patient;
  const signatureStatus = location.state.document?.status;
  // THIN-7204: prefer signatureContext.supervisor.signStatus; fall back to the legacy
  // supervisorStatus DTO field for older payloads that pre-date the THIN-7160 endpoint.
  // The CodeSubmission payload still carries supervisorStatus for BE compatibility.
  const supervisorStatus =
    location.state.document?.signatureContext?.supervisor?.signStatus
    ?? location.state.document?.supervisorStatus;
  const getAccessToken = async () => ''; // Don't need this, so making it nothing

  const formSubmissionUuid = location?.state?.document?.uuid || false;

  useEffect(() => {
    if (formStatus === 'loading') {
      if (realm && therapistProfile && formSubmissionUuid) {
        DocumentService.getIcdCodes(realm, formSubmissionUuid)
          .then((response) => {
            if (response.error) {
              setFormStatus('error');
              setErrorMessage(response.error);
            } else {
              setPrefill(response);
              setFormStatus('ready');
            }
          })
          .catch((error) => {
            console.error('error', error);
            setFormStatus('error');
          });
      }
    }
  }, [formStatus, realm, therapistProfile, location.state, formSubmissionUuid]);

  const submitForm = async (data: JsonFormsData) => {
    setFormStatus('submitting');
    const ipAddress = localStorage.getItem('ip-address') || '';
    const icd10TreatmentCodes: IcdCode[] = data.icd10TreatmentCodes || [];
    const icd10TMedicalCodes: IcdCode[] = data.icd10TMedicalCodes || [];
    const submission: CodeSubmission = {
      ipAddress,
      signatureStatus,
      supervisorStatus,
      icd10TreatmentCodes,
      icd10TMedicalCodes,
    };

    if (doNotDisplayChecked) {
      localStorage.setItem(`doNotDisplay${providerId}`, 'true');
    }

    if (realm && therapistProfile && formSubmissionUuid && facilityId) {
      try {
        const response = await DocumentService.submitIcdCodes(
          realm,
          location.state.document.uuid,
          facilityId,
          submission
        );

        if (response && response.error === false) {
          datadogRum.addAction('medicalCodes_submitForm_SUCCESS', { realm });
          setFormStatus('success');
        } else {
          datadogRum.addAction('medicalCodes_submitForm_FAILED', { realm, error: response.error });
          setErrorMessage(response.error || 'Failed to submit codes');
          setFormStatus('error');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        datadogRum.addAction('medicalCodes_submitForm_ERROR', { realm, error: error.message });
        setErrorMessage('An error occurred while submitting the form');
        setFormStatus('error');
      }
    }
  };

  const submitCallback = (data: JsonFormsData) => {
    setFormData(data);

    if (doNotDisplayDialog()) {
      submitForm(data);
    } else {
      setDialogOpen(true);
    }
  };

  const navigateToEpisodeOfCare = () => {
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
      state: {
        episodeOfCareTitle: location.state.episodeOfCareTitle,
        episodeOfCareId: location.state.episodeOfCareId,
        getPatientId: location.state.patient.uuid,
      },
    });
  };

  const handleSuccess = () => {
    navigateToEpisodeOfCare();
  };

  const handleError = () => {
    navigateToEpisodeOfCare();
  };

  let component;

  switch (formStatus) {
    case 'loading':
      component = <CommonLoader />;
      break;

    case 'ready':
      component = (
        <ReForm
          schema={schema}
          uiSchema={uischema}
          submitCallback={submitCallback}
          initialData={prefill}
          formUuid=""
          realm=""
          apiUrl=""
          aiApiUrl=""
          codesApiUrl={codesApiUrl}
          codesApiSecurityCode={codesApiSecurityCode}
          getAccessToken={getAccessToken}
          facilityId={facilityId || ''}
          discipline={therapistProfile.discipline}
          featureToggles={{
            icd10Excludes1Warning: excludes1WarnEnabled || false,
            icd10RtpWarning: icd10ReturnToProviderEnabled || false,
          }}
        />
      );
      break;

    case 'submitting':
      component = <CommonLoader />;
      break;

    case 'success':
      component = (
        <SucessModal
          title="Submitted"
          message="Your submission has been received"
          close={handleSuccess}
        />
      );
      break;

    default:
      component = (
        <SucessModal
          title="Error"
          message={errorMessage}
          isSucess={false}
          close={handleError}
        />
      );

      break;
  }

  if (therapistProfile.currentRoles && !permissionData.includes(EDIT_MEDICAL_CODE)) {
    navigate(paths.dashboard);
  }

  const handleCancelClick = () => {
    setDialogOpen(false);
  };

  const handleSaveClick = () => {
    submitForm(formData);
    setDialogOpen(false);
  };

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDoNotDisplayChecked(event.target.checked);
  };

  const dateOfBirth = patientData?.dateOfBirth
    ? moment(new Date(patientData.dateOfBirth)).format('YYYY-MM-DD')
    : '';

  const patientInfo = {
    name: patientData?.name || '',
    dob: dateOfBirth,
    mrn: patientData?.medicalRecordNumber || '',
    eocTitle: location.state.episodeOfCareTitle || '',
  };

  return (
    <>
      <Dialog
        maxWidth="md"
        open={dialogOpen}
      >
        <DialogTitle>IMPORTANT</DialogTitle>

        <DialogContent dividers>
          <Typography gutterBottom>
            <ul>
              <li>{`You've updated the ICD-10 code(s) for this evaluation.`}</li>
              <li>{`This change applies to this evaluation and all new documents moving forward.`}</li>
              <li>{`Previously submitted documents (like progress notes) will not change.`}</li>
              <li>{`The document owner and physician will need to re-sign this evaluation to confirm the update.`}</li>
            </ul>
          </Typography>
        </DialogContent>

        <DialogActions>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                onChange={handleToggle}
                checked={doNotDisplayChecked}
              />
            }
            label="Do not show again"
            sx={{
              flexGrow: 1,
              marginLeft: '0',
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem', // or '14px'
                lineHeight: 1,
              },
            }}
          />

          <Button
            autoFocus
            onClick={handleCancelClick}
            disabled={dialogDisabled}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSaveClick}
            disabled={dialogDisabled}
            variant="contained"
          >
            Submit and Sign
          </Button>
        </DialogActions>
      </Dialog>

      <Container maxWidth={settings.stretch ? false : 'xl'}>
        <Button
          sx={{
            justifyContent: 'flex-start',
            color: '#111927',
            width: 'max-content',
            padding: '0px',
          }}
          onClick={navigateToEpisodeOfCare}
          startIcon={
            <SvgIcon>
              <ArrowBackIcon sx={{ color: '#6C737F' }} />
            </SvgIcon>
          }
        >
          <Typography variant="subtitle1">
            Back to Episode Of Care Details -
            {` ${patientData?.lastName ?? ''} ${patientData?.firstName ?? ''}`}
          </Typography>
        </Button>
      </Container>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 8,
        }}
      >
        <Container maxWidth={settings.stretch ? false : 'xl'}>
          <Typography
            variant="h5"
            gutterBottom
          >
            Edit ICD-10 Codes
          </Typography>

          <DetailsContainer>
            <PatientInfoContainer>
              <div>
                <strong>Patient:</strong> {patientInfo.name}
              </div>
              <div>
                <strong>DOB:</strong> {patientInfo.dob}
              </div>
              <div>
                <strong>MRN:</strong> {patientInfo.mrn}
              </div>
              <div>
                <strong>Episode of Care:</strong> {patientInfo.eocTitle}
              </div>
            </PatientInfoContainer>
          </DetailsContainer>
          {component}
        </Container>
      </Box>
    </>
  );
};

export default MedicalCodeEditor;
